import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import type { Ship } from "../ship/Ship";
import type { Track } from "../track/Track";

/** Which rig drives the view: "gx" lines up with the road (slope and bank) like
 * F-Zero GX; "classic" is the original world-level rig. Toggled from the pause
 * menu and remembered. */
export type CameraMode = "gx" | "classic";

const MODE_KEY = "driftracer.camera";
let mode: CameraMode = loadMode();

function loadMode(): CameraMode {
  try {
    return localStorage.getItem(MODE_KEY) === "classic" ? "classic" : "gx";
  } catch {
    return "gx";
  }
}

export function getCameraMode(): CameraMode {
  return mode;
}

export function setCameraMode(m: CameraMode): void {
  mode = m;
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    /* storage blocked — the choice just won't persist */
  }
}

/** Time constant (s) for the camera yaw chasing the travel direction. Small =
 * snappy/responsive in corners; just enough to smooth the grip-catch swing. */
const YAW_TAU = 0.09;
/** Fixed amount the look-target sits below the camera — sets a constant downward
 * pitch (never swings with speed/elevation). Tuned so the craft sits in the
 * lower third with track visible below it, F-Zero-GX style. */
const LOOK_DROP = 3.3;
/** Minimum camera height above the track surface beneath it (anti clip-through). */
const GROUND_CLEARANCE = 1.6;

/** How far ahead of the craft (world units, along the road) the GX rig reads
 * the road's "up". Reading ahead lets it tip over a crest or into a loop as you
 * reach it, instead of see-sawing with the surface right underneath. */
const UP_AHEAD = 15;
/** Time constant (s) for easing onto the road's slope and bank. */
const ALIGN_TAU = 0.18;
/** The most (rad) the rig's up may trail the craft's on the road. */
const MAX_UP_LAG = 0.12;
/** Time constant (s) for levelling out while airborne: in flight the view holds
 * steady instead of following the hull's nose. */
const AIR_LEVEL_TAU = 0.6;
/** The classic rig is world-level; past this much tilt (a wall, a loop) the GX
 * rig takes over whichever is picked. */
const CLASSIC_MAX_TILT = 0.85;

/**
 * F-Zero-GX-style chase cam: a tight, RIGID offset behind the craft (constant
 * distance, so framing never changes and it reacts instantly — no speed lag),
 * oriented to the direction of travel (so a sideways drift slides across the
 * frame), held above the track so it never sinks into hills. Speed is sold by
 * FOV + the grid/streaks, never by moving the camera.
 *
 * The GX rig is that same offset and look, laid in the road's own frame: it
 * pitches with the slope ahead and rolls with the bank. On flat, level road the
 * two rigs are identical.
 */
export class ChaseCamera {
  readonly camera: UniversalCamera;

  private readonly distance = 7;
  private readonly height = 3.5;
  private readonly baseFov = 0.9;
  private readonly maxFov = 1.04;

  private camYaw = 0;
  private initialized = false;
  /** GX: the rig's eased frame — its up (the road's, or world up in the air) and
   * forward (the direction of travel, kept square to that up). */
  private readonly camUp = new Vector3(0, 1, 0);
  private readonly camFwd = new Vector3(0, 0, 1);
  private readonly mat = new Matrix();

  constructor(scene: Scene) {
    this.camera = new UniversalCamera("chase", new Vector3(0, 10, -20), scene);
    this.camera.fov = this.baseFov;
    // Far enough that from high on a course the sea/ground runs out into the
    // fog, not into a visible edge; near nudged out to keep depth precision.
    this.camera.minZ = 0.5;
    this.camera.maxZ = 4500;
    this.camera.inputs.clear(); // driven manually
    this.camera.rotationQuaternion = new Quaternion();
  }

  update(dt: number, ship: Ship, track: Track): void {
    const speed = ship.velocity.length();

    // Orient to the DIRECTION OF TRAVEL (velocity), falling back to heading when
    // nearly stopped. This is what lets the ship sit sideways in a drift while
    // the view stays pointed where you're going. Only the yaw is smoothed —
    // position is rigid — so there's no distance lag and it snaps to corners.
    const targetYaw = speed > 4 ? Math.atan2(ship.velocity.x, ship.velocity.z) : ship.yaw;
    const first = !this.initialized;
    if (first) {
      this.camYaw = targetYaw;
      this.initialized = true;
    } else {
      let d = targetYaw - this.camYaw;
      d = Math.atan2(Math.sin(d), Math.cos(d)); // shortest arc
      this.camYaw += d * (1 - Math.exp(-dt / YAW_TAU));
    }

    // The GX rig keeps its frame running either way, so switching is seamless.
    this.updateGx(dt, ship, track, first);
    if (mode === "classic" && ship.up.y > CLASSIC_MAX_TILT) this.updateClassic(ship, track);

    // FOV nudges up with speed for the rush, without shrinking the ship.
    const targetFov = this.baseFov + (this.maxFov - this.baseFov) * Math.min(1, ship.speedRatio);
    this.camera.fov += (targetFov - this.camera.fov) * Math.min(1, 5 * dt);
  }

  /** The original rig: world-level, with a fixed gentle downward look. */
  private updateClassic(ship: Ship, track: Track): void {
    const ratio = ship.speedRatio;
    const forward = new Vector3(Math.sin(this.camYaw), 0, Math.cos(this.camYaw));

    // Rigid offset behind + above the ship — constant distance at all speeds.
    const pos = ship.position.subtract(forward.scale(this.distance));
    pos.y = ship.position.y + this.height;

    // Keep clear of the track surface beneath the camera (handles hills/boost so
    // it can never clip through the floor), and never below the ship either.
    const groundUnderCam = track.locate(pos).height;
    const minY = Math.max(groundUnderCam, ship.position.y) + GROUND_CLEARANCE;
    if (pos.y < minY) pos.y = minY;
    this.camera.position.copyFrom(pos);

    // Look ahead along travel, at a fixed gentle downward pitch (target height
    // tied to the camera height) so the view never tilts up/down on its own.
    const target = ship.position.add(forward.scale(7 + ratio * 2));
    target.y = pos.y - LOOK_DROP;
    this.camera.setTarget(target);
  }

  /** The classic rig laid in the road's own frame: its up eases to the road's
   * up a little ahead (so it pitches with slopes, rolls with banks and turns
   * right over in a loop), its forward follows the direction of travel, and in
   * the air it levels out. On flat, level road it's the classic rig exactly. */
  private updateGx(dt: number, ship: Ship, track: Track, first: boolean): void {
    const ratio = ship.speedRatio;

    // Where the rig wants its up: the road's, read a little ahead (the way the
    // craft is going) at the craft's place across it (round the side of a
    // pipe, that's the side's), or world up in the air.
    let targetUp: Vector3 = Vector3.Up();
    if (!ship.airborne && ship.road) {
      const dir = Vector3.Dot(ship.velocity, ship.road.forward) >= 0 ? 1 : -1;
      targetUp = track.frameAt(ship.trackT + (dir * UP_AHEAD) / track.length, ship.road.u).up;
    }
    // Where it wants to face: the direction of travel (heading when nearly
    // stopped) — the same yaw the classic rig chases.
    const speed = ship.velocity.length();
    const travel = speed > 4 ? ship.velocity.clone() : ship.heading.clone();
    if (ship.airborne) travel.y = 0;

    if (first) {
      this.camUp.copyFrom(targetUp);
      this.camFwd.copyFrom(travel);
    } else {
      const k = 1 - Math.exp(-dt / (ship.airborne ? AIR_LEVEL_TAU : ALIGN_TAU));
      this.camUp.addInPlace(targetUp.subtract(this.camUp).scaleInPlace(k));
    }
    this.camUp.normalize();
    // On the road the ease may only trail the craft's own up by a few degrees:
    // gentle on slopes and banks, but locked on through a loop or a corkscrew,
    // where the road turns over far faster than any ease can follow.
    if (!ship.airborne) {
      const lag = Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(this.camUp, ship.up))));
      if (lag > MAX_UP_LAG) {
        const t = 1 - MAX_UP_LAG / lag;
        this.camUp.addInPlace(ship.up.subtract(this.camUp).scaleInPlace(t)).normalize();
      }
    }
    // Keep forward square to up, then turn it toward the travel direction
    // within that plane, eased like the classic rig's yaw.
    const square = (v: Vector3) => {
      v.subtractInPlace(this.camUp.scale(Vector3.Dot(v, this.camUp)));
      return v.lengthSquared() > 1e-9 ? v.normalize() : null;
    };
    const want = square(travel);
    if (!square(this.camFwd)) this.camFwd.copyFrom(want ?? ship.heading);
    if (want && !first) {
      const a = Math.atan2(Vector3.Dot(Vector3.Cross(this.camFwd, want), this.camUp), Vector3.Dot(this.camFwd, want));
      const turn = a * (1 - Math.exp(-dt / YAW_TAU));
      const c = Math.cos(turn);
      const s = Math.sin(turn);
      this.camFwd
        .scaleInPlace(c)
        .addInPlace(Vector3.Cross(this.camUp, this.camFwd).scaleInPlace(s))
        .normalize();
    } else if (want) {
      this.camFwd.copyFrom(want);
    }
    const fwd = this.camFwd;
    const up = this.camUp;

    // Same rig as classic (behind + above, look just above the craft), in that frame.
    const pos = ship.position.subtract(fwd.scale(this.distance)).addInPlace(up.scale(this.height));
    const target = ship.position.add(fwd.scale(7 + ratio * 2)).addInPlace(up.scale(this.height - LOOK_DROP));

    // Keep clear of the road under the camera. If the craft has dropped below
    // the road (a missed jump), follow it down rather than losing it off the
    // bottom of the frame. Lifting moves the look point too, so the view
    // direction holds.
    const under = track.project(pos, ship.roadHint);
    const falling = ship.airborne && (ship.road?.h ?? 0) < 0;
    if (!falling && Math.abs(under.u) <= under.halfWidth && under.h < GROUND_CLEARANCE) {
      const lift = under.up.scale(GROUND_CLEARANCE - under.h);
      pos.addInPlace(lift);
      target.addInPlace(lift);
    }
    this.camera.position.copyFrom(pos);

    // Aim at the look point with the rig's up, keeping the roll.
    const look = target.subtract(pos).normalize();
    const right = Vector3.Cross(up, look).normalize();
    const camUp = Vector3.Cross(look, right);
    Matrix.FromXYZAxesToRef(right, camUp, look, this.mat);
    Quaternion.FromRotationMatrixToRef(this.mat, this.camera.rotationQuaternion!);
  }

  snapTo(ship: Ship, track: Track): void {
    this.initialized = false;
    this.update(0.016, ship, track);
  }
}
